// Thin fetch wrapper for /api/admin/audit/* endpoints (v5 redesign).
//
// Mirrors the shape of roleClient.ts: pure HTTP, no business logic. The
// new BFF surface is being built in parallel against LLD §8; until it is
// deployed, the client falls back to the legacy `/api/admin/audit?date=`
// endpoint and synthesizes a v5-shaped response so the UI continues to
// render. Once the new endpoints respond, the fallback becomes dead code.
//
// Subscriptions CRUD (Phase 6) lives at the same /api/admin/audit prefix.
// The runner Lambda + SES delivery is OUT OF SCOPE for Phase 6 — the UI
// only stores rows. See the banner on Subscriptions.tsx.

import { request, ApiError } from "@/api";
import { adminGet } from "@/data/httpClient";
import type {
  AuditEvent,
  AuditEventDetail,
  AuditFacetsResponse,
  AuditListResponse,
} from "@/pages/admin/audit/types";

export interface ListEventsParams {
  from: string;
  to: string;
  locationIds?: string[];
  eventTypes?: string[];
  actorAliases?: string[];
  actorKind?: string;
  q?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
}

// ---------------------------------------------------------------------------
// Subscriptions (Phase 6 — CRUD only; runner Lambda + SES are deferred)
// ---------------------------------------------------------------------------

/** Filter spec persisted on a subscription row. Mirrors the audit list
 *  query shape — every field is optional; an empty spec means "all events". */
export interface AuditSubscriptionFilterSpec {
  from?: string;
  to?: string;
  location_ids?: string[];
  event_types?: string[];
  actor_aliases?: string[];
  q?: string;
}

/** Cadence values accepted by the BFF (LLD §12.2). */
export type AuditSubscriptionCadence = "DAILY" | "WEEKLY" | "MONTHLY";

/** Status values managed by the UI (LLD §12.2). */
export type AuditSubscriptionStatus = "ACTIVE" | "PAUSED";

/**
 * Subscription row as returned by the BFF (see
 * AuditSubscriptionsActivities.renderItem). Field names are snake_case
 * to match the wire shape verbatim — DebugPanel dumps them unmodified.
 */
export interface AuditSubscription {
  id: string;
  tenant_id: string;
  owner_alias: string;
  cadence: AuditSubscriptionCadence;
  email_to: string;
  status: AuditSubscriptionStatus;
  next_run_at: string;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
  filter_spec: AuditSubscriptionFilterSpec;
}

export interface AuditSubscriptionListResponse {
  subscriptions: AuditSubscription[];
}

export interface CreateSubscriptionInput {
  cadence: AuditSubscriptionCadence;
  email_to: string;
  filter_spec: AuditSubscriptionFilterSpec;
}

export interface UpdateSubscriptionInput {
  cadence?: AuditSubscriptionCadence;
  email_to?: string;
  filter_spec?: AuditSubscriptionFilterSpec;
  status?: AuditSubscriptionStatus;
}

export interface AuditClient {
  /** Returns null when the new endpoint is not yet deployed (404). */
  listEvents(params: ListEventsParams): Promise<AuditListResponse | null>;
  /** Returns null when the new endpoint is not yet deployed (404). */
  getFacets(params: ListEventsParams): Promise<AuditFacetsResponse | null>;
  getEventDetail(eventId: string): Promise<AuditEventDetail | null>;
  /** Legacy single-day endpoint, still supported until BFF Phase 3 lands. */
  legacyListByDate(date: string): Promise<AuditListResponse>;

  // --- Subscriptions CRUD ---
  listSubscriptions(): Promise<AuditSubscriptionListResponse>;
  createSubscription(input: CreateSubscriptionInput): Promise<AuditSubscription>;
  updateSubscription(
    id: string,
    patch: UpdateSubscriptionInput,
  ): Promise<AuditSubscription>;
  deleteSubscription(id: string): Promise<void>;

  // --- Exports (Phase 5) ---
  createExport(input: CreateExportInput): Promise<CreateExportResponse>;
  listExports(): Promise<AuditExportListResponse>;
  getExport(exportId: string): Promise<AuditExport | null>;
}

// ---------------------------------------------------------------------------
// Exports (Phase 5 — POST/GET against /api/admin/audit/exports)
// ---------------------------------------------------------------------------

/** Filter spec persisted on an export job. Identical shape to a
 *  subscription's filter_spec; pass empty arrays for "all events". */
export interface AuditExportFilterSpec {
  from: string;
  to: string;
  locations?: string[];
  event_types?: string[];
  actor_aliases?: string[];
  q?: string;
  include?: "filtered" | "all";
}

export type AuditExportStatus =
  | "queued"
  | "running"
  | "ready"
  | "failed"
  | "expired";

export interface AuditExport {
  exportId: string;
  status: AuditExportStatus;
  createdAt: string;
  createdBy: string;
  actingRole?: string;
  name: string;
  filterSpec: AuditExportFilterSpec | null;
  finishedAt?: string | null;
  expiresAt?: string | null;
  sizeBytes: number;
  rowCount: number;
  errorMessage?: string | null;
  /** Fresh presigned S3 URL when status="ready". Otherwise null. */
  downloadUrl?: string | null;
}

export interface AuditExportListResponse {
  exports: AuditExport[];
}

export type CreateExportInput = AuditExportFilterSpec;

export interface CreateExportResponse {
  export_id: string;
  status: AuditExportStatus;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildQuery(params: ListEventsParams): string {
  const usp = new URLSearchParams();
  usp.set("from", params.from);
  usp.set("to", params.to);
  for (const id of params.locationIds ?? []) usp.append("location_id", id);
  for (const t of params.eventTypes ?? []) usp.append("event_type", t);
  for (const a of params.actorAliases ?? []) usp.append("actor_alias", a);
  if (params.actorKind) usp.set("actor_kind", params.actorKind);
  if (params.q) usp.set("q", params.q);
  if (params.sort) usp.set("sort", params.sort);
  if (params.page !== undefined) usp.set("page", String(params.page));
  if (params.pageSize !== undefined) usp.set("page_size", String(params.pageSize));
  return usp.toString();
}

/** Detect "endpoint doesn't exist" — the BFF returns 404, the Coral router
 *  returns an `UnknownOperationException` body with status 400 or 404. */
function isEndpointMissing(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 404) return true;
  if (typeof err.body === "string" && err.body.includes("UnknownOperation")) {
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Legacy → v5 shape adapter
// ---------------------------------------------------------------------------

interface LegacyAuditEvent {
  eventId: string;
  timestamp: string;
  eventType: string;
  actor: string;
  target: string;
  detail: Record<string, string>;
}

/** Best-effort upcast of the legacy v0 row shape to the v5 list shape so the
 *  new UI renders even before the BFF agent finishes. Heuristics-only. */
function adaptLegacyEvent(e: LegacyAuditEvent): AuditEvent {
  const category = inferCategory(e.eventType);
  return {
    eventId: e.eventId,
    timestamp: e.timestamp,
    eventType: e.eventType,
    category,
    humanLabel: humanizeEventType(e.eventType),
    rawTarget: e.target,
    actor: {
      alias: e.actor,
      displayName: e.actor,
      kind: e.actor === "system" ? "system" : "user",
      role: e.detail?.role_used ?? "",
    },
    target: e.target
      ? { kind: "unknown", id: e.target, displayName: e.target }
      : undefined,
    locationId: e.detail?.location_id,
    locationName: e.detail?.location_id,
    requestId: e.detail?.request_id,
    sourceIp: e.detail?.source_ip,
    changes: undefined,
    detailsText: summarizeDetail(e.detail),
  };
}

function inferCategory(t: string): string {
  if (t.startsWith("ROLE")) return "ROLE";
  if (t.startsWith("CAMERA")) return "CAMERA";
  if (t.startsWith("VIDEO") || t.startsWith("LIVE")) return "VIDEO";
  if (t.startsWith("MODE")) return "MODE";
  if (t.startsWith("LOCATION")) return "LOCATION";
  if (t.startsWith("INSTALLER") || t.startsWith("DEVICE")) return "INSTALLER";
  if (t.startsWith("ALARM") || t.startsWith("ACCESS")) return "ACCESS";
  if (t.startsWith("SESSION") || t.startsWith("LOGIN")) return "SESSION";
  return "IDENTITY";
}

function humanizeEventType(t: string): string {
  return t
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/^\w/, (c) => c.toUpperCase());
}

function summarizeDetail(d: Record<string, string> | undefined): string {
  if (!d) return "";
  const keys = Object.keys(d);
  if (keys.length === 0) return "";
  if (keys.length <= 2) {
    return keys.map((k) => `${k}: ${d[k]}`).join(" · ");
  }
  return `${keys.length} fields`;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

function createAuditClient(): AuditClient {
  const BASE = "/api/admin/audit";

  return {
    async listEvents(params) {
      const qs = buildQuery(params);
      try {
        return await adminGet<AuditListResponse>(`${BASE}/events?${qs}`);
      } catch (err) {
        if (isEndpointMissing(err)) return null;
        throw err;
      }
    },

    async getFacets(params) {
      const qs = buildQuery(params);
      try {
        return await adminGet<AuditFacetsResponse>(`${BASE}/facets?${qs}`);
      } catch (err) {
        if (isEndpointMissing(err)) return null;
        throw err;
      }
    },

    async getEventDetail(eventId) {
      try {
        return await adminGet<AuditEventDetail>(
          `${BASE}/events/${encodeURIComponent(eventId)}`,
        );
      } catch (err) {
        if (isEndpointMissing(err)) return null;
        throw err;
      }
    },

    async legacyListByDate(date) {
      const resp = await adminGet<{ events: LegacyAuditEvent[] }>(
        `${BASE}?date=${encodeURIComponent(date)}`,
      );
      const events = (resp.events ?? []).map(adaptLegacyEvent);
      return {
        events,
        page: 1,
        page_size: events.length,
        total: events.length,
        total_pages: 1,
        truncated: false,
      };
    },

    // ---------- Subscriptions CRUD ----------
    //
    // No If-Match version on these — `AuditSubscriptions` rows aren't
    // versioned (LLD §12.1 — last-writer-wins is acceptable for the runner
    // schedule). Uses the `request` core directly so we don't accidentally
    // pick up the optimistic-concurrency machinery in `adminPatch`.
    async listSubscriptions() {
      return adminGet<AuditSubscriptionListResponse>(`${BASE}/subscriptions`);
    },

    async createSubscription(input) {
      const res = await request<AuditSubscription>(`${BASE}/subscriptions`, {
        method: "POST",
        body: input,
      });
      if (res === null) {
        throw new ApiError(0, "aborted", "request aborted", undefined, null);
      }
      return res.data;
    },

    async updateSubscription(id, patch) {
      const res = await request<AuditSubscription>(
        `${BASE}/subscriptions/${encodeURIComponent(id)}`,
        { method: "PATCH", body: patch },
      );
      if (res === null) {
        throw new ApiError(0, "aborted", "request aborted", undefined, null);
      }
      return res.data;
    },

    async deleteSubscription(id) {
      const res = await request<unknown>(
        `${BASE}/subscriptions/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      );
      if (res === null) {
        throw new ApiError(0, "aborted", "request aborted", undefined, null);
      }
    },

    // ---------- Exports (Phase 5) ----------
    async createExport(input) {
      const res = await request<CreateExportResponse>(`${BASE}/exports`, {
        method: "POST",
        body: input,
      });
      if (res === null) {
        throw new ApiError(0, "aborted", "request aborted", undefined, null);
      }
      return res.data;
    },

    async listExports() {
      return adminGet<AuditExportListResponse>(`${BASE}/exports`);
    },

    async getExport(exportId) {
      try {
        return await adminGet<AuditExport>(
          `${BASE}/exports/${encodeURIComponent(exportId)}`,
        );
      } catch (err) {
        if (isEndpointMissing(err)) return null;
        throw err;
      }
    },
  };
}

export const auditClient: AuditClient = createAuditClient();
